import React, { createContext, useContext, useState, useCallback } from 'react';
import ErrorNotification from '../components/ErrorNotification';

interface NotificationItem {
  id: string;
  message: string;
  type: 'error' | 'success' | 'warning';
  autoClose?: boolean;
  duration?: number;
}

interface ErrorContextType {
  showError: (message: string, autoClose?: boolean, duration?: number) => void;
  showSuccess: (message: string, autoClose?: boolean, duration?: number) => void;
  showWarning: (message: string, autoClose?: boolean, duration?: number) => void;
  clearNotification: (id: string) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: React.ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const addNotification = useCallback((notification: Omit<NotificationItem, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications(prev => [...prev, { ...notification, id }]);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showError = useCallback((message: string, autoClose = true, duration = 5000) => {
    addNotification({ message, type: 'error', autoClose, duration });
  }, [addNotification]);

  const showSuccess = useCallback((message: string, autoClose = true, duration = 3000) => {
    addNotification({ message, type: 'success', autoClose, duration });
  }, [addNotification]);

  const showWarning = useCallback((message: string, autoClose = true, duration = 4000) => {
    addNotification({ message, type: 'warning', autoClose, duration });
  }, [addNotification]);

  const clearNotification = useCallback((id: string) => {
    removeNotification(id);
  }, [removeNotification]);

  return (
    <ErrorContext.Provider value={{ showError, showSuccess, showWarning, clearNotification }}>
      {children}
      {/* Render all notifications */}
      {notifications.map((notification) => (
        <ErrorNotification
          key={notification.id}
          message={notification.message}
          type={notification.type}
          autoClose={notification.autoClose}
          duration={notification.duration}
          onClose={() => clearNotification(notification.id)}
        />
      ))}
    </ErrorContext.Provider>
  );
};
